/* ============================================================
   Deploy dialogs: validation, compose/manifest editor, add workload,
   create VM/LXC wizard, import existing machine, install software,
   deploy / edit a stack (port of modals.js 1–392)
   ============================================================ */
import { Fragment, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { DB, api, can, fire, job, node, nodeOf, site, toast, wl, type Any } from "../lib/store";
import { Copyable } from "../ui/common";
import { archFits, archOf } from "../lib/arch";
import { F, FormCtx, Frame, Modal, startWizard, type Form } from "../ui/modal";

/* ============================ VALIDATION (server-side) ============================ */
export function Validation({ res }: { res: Any }) {
  if (!res)
    return (
      <div className="vres"><div className="vh">Not validated yet</div>
        <ul><li><span className="tag wrn">todo</span><span>Click Validate — the console parses the YAML, applies your deployment policy and runs a real dry run.</span></li></ul>
      </div>
    );
  if (res.pending) return <div className="vres"><div className="vh"><span className="spin" /> Validating…</div></div>;
  if (res._error != null) return <div className="vres"><div className="vh">{res._error}</div></div>;
  const errors: Any[] = res.errors || [], warnings: Any[] = res.warnings || [], info: Any[] = res.info || [];
  const ok = errors.length === 0;
  return (
    <div className="vres">
      <div className="vh" style={{ background: ok ? "var(--ok-soft)" : "var(--bad-soft)", color: ok ? "var(--ok)" : "var(--bad)" }}>
        {ok ? `Valid — ${warnings.length} warning${warnings.length === 1 ? "" : "s"} to look at`
          : `${errors.length} error${errors.length === 1 ? "" : "s"} must be fixed before deploying`}
      </div>
      <ul>
        {errors.map((e, i) => <li key={"e" + i}><span className="tag err">line {e.line}</span><span>{e.msg}</span></li>)}
        {warnings.map((w, i) => <li key={"w" + i}><span className="tag wrn">warn</span><span>{w.msg}</span></li>)}
        {info.map((x, i) => <li key={"i" + i}><span className="tag ok">pass</span><span>{x.msg}</span></li>)}
      </ul>
    </div>
  );
}
export async function validateText(text: string, kind: string, env?: string, workload?: Any, stack?: Any, relaxed?: boolean) {
  return api("POST", "/api/validate", { text, kind, env: env || "", workload: workload || null, stack: stack || null, relaxed: !!relaxed });
}
/* Admin-only "relaxed validation" switch shown under every compose/manifest editor */
export const canRelax = () => (can("Admin") || DB.me.platform_admin) && (DB.settings.policy || {}).allow_override !== false;

function RelaxBox({ f, kind }: { f: Form; kind: string }) {
  if (!canRelax()) return null;
  const on = !!f.d.relaxed;
  return (
    <label className={"check " + (on ? "on" : "")} style={{ marginTop: 10 }}>
      <input type="checkbox" checked={on}
        onChange={(e) => { f.d.relaxed = e.target.checked; f.d._val = null; f.refresh(); fire(wizValidate(f, kind, true)); }} />
      <span><b>Relaxed validation for this deploy (Admin override)</b><small>Uses the relaxed profile: missing memory limits, :latest tags, host networking and privileged containers become warnings instead of errors. Recorded in the audit log and the stack version note.</small></span>
    </label>
  );
}

export const SAMPLE_COMPOSE = `services:
  web:
    image: nginx:1.27-alpine
    restart: unless-stopped
    ports:
      - "8080:80"
    depends_on:
      - db
    mem_limit: 128m

  db:
    image: postgres:16.4-alpine
    restart: unless-stopped
    environment:
      POSTGRES_PASSWORD: \${DB_PASSWORD}
      POSTGRES_DB: app
    volumes:
      - pgdata:/var/lib/postgresql/data
    mem_limit: 512m

  cache:
    image: redis:7.4-alpine
    restart: unless-stopped
    mem_limit: 128m

volumes:
  pgdata:
`;
export const SAMPLE_ENV = `DB_PASSWORD=change-me\nTZ=Asia/Kolkata`;
export const SAMPLE_K8S = `apiVersion: apps/v1
kind: Deployment
metadata:
  name: web
spec:
  replicas: 2
  selector:
    matchLabels: { app: web }
  template:
    metadata:
      labels: { app: web }
    spec:
      containers:
        - name: nginx
          image: nginx:1.27-alpine
          ports:
            - containerPort: 80
          resources:
            requests: { cpu: 50m, memory: 32Mi }
            limits: { memory: 128Mi }
---
apiVersion: v1
kind: Service
metadata:
  name: web
spec:
  selector: { app: web }
  ports:
    - port: 80
      targetPort: 80
`;

/* ---------- wizard form helpers ---------- */
/* validation result lives in f.d._val (read by Review steps and yamlGate); the box state
   (pending / request error) lives in f.d._vst so any handler can trigger a re-validate */
export async function wizValidate(f: Form, kind?: string, quiet?: boolean) {
  const seq = (f.d._vseq = (f.d._vseq || 0) + 1);
  f.d._vst = "pending"; f.refresh();
  try {
    const r = await validateText(f.d.yaml, kind || "compose", f.d.env, f.d._wid, f.d._sid, f.d.relaxed);
    if (seq === f.d._vseq) { f.d._val = r; f.d._vst = null; f.refresh(); }
    if (!quiet) toast("Validation finished", r.errors.length ? `${r.errors.length} error(s)` : "Schema, policy and dry run passed");
    return r;
  } catch (e: Any) {
    if (seq === f.d._vseq) { f.d._vst = { _error: e.message }; f.refresh(); }
    throw e;
  }
}
export async function yamlGate(d: Any, kind: string) {
  const r = await validateText(d.yaml, kind, d.env, d._wid, d._sid, d.relaxed);
  d._val = r;
  return r.errors.length ? `Fix ${r.errors.length} error(s) first: ${r.errors[0].msg}` : null;
}

/* the old editor(): YAML textarea + Validate button + relaxed switch + result box.
   `auto` re-validates quietly when the step is shown (the old step.after hook). */
export function YamlEditor({ f, k = "yaml", file, kind, extra, auto }:
  { f: Form; k?: string; file: ReactNode; kind: string; extra?: ReactNode; auto?: boolean }) {
  useEffect(() => { if (auto) fire(wizValidate(f, kind, true)); }, []);
  const vst = f.d._vst;
  const shown = vst === "pending" ? { pending: true } : vst && vst._error != null ? vst : f.d._val;
  return (
    <>
      <div className="editor">
        <div className="eh"><b>{file}</b><div className="spacer" />{extra}
          <button className="btn sm" onClick={() => fire(wizValidate(f, kind))}>Validate</button></div>
        <textarea spellCheck={false} value={f.d[k] ?? ""} onChange={(e) => { f.d._val = null; f.set(k, e.target.value); }} />
      </div>
      <RelaxBox f={f} kind={kind} />
      <div id="wizval"><Validation res={shown} /></div>
    </>
  );
}

/* a select that, like the old DOM collect(), takes the first option when the stored value is not one of the options */
type Opt = string | number | [Any, ReactNode];
function Sel(p: { k: string; label: ReactNode; opts: Opt[]; hint?: ReactNode }) {
  const f = useContext(FormCtx)!;
  const vals = p.opts.map((o) => (Array.isArray(o) ? o[0] : o));
  if (vals.length && !vals.some((v) => String(v) === String(f.d[p.k]))) f.d[p.k] = vals[0];
  return <F.Sel {...p} />;
}
/* per-mount defaults: the old step templates showed `d.x || default` and collected it on Continue */
function Init({ f, run, children }: { f: Form; run: (d: Any) => void; children: ReactNode }) {
  const done = useRef(false);
  if (!done.current) { done.current = true; run(f.d); }
  return <>{children}</>;
}

/* ---- new workload ---- */
export function addWorkload(nodeId?: Any) {
  const items: [string, string, string][] = [
    ["vm", "Create a virtual machine", "Full OS with its own kernel. Best for anything that needs Docker, Windows, or isolation."],
    ["lxc", "Create an LXC container", "Lighter and faster to start. Nesting and keyctl are switched on automatically if you want Docker inside."],
    ["import", "Import a machine that already exists", "Point at an IP you already run. We test the connection, detect the OS and take it under management."],
  ];
  Modal.open(
    <Frame title="Add a workload" footer={<><div className="spacer" /><button className="btn" onClick={Modal.close}>Cancel</button></>}>
      <p style={{ marginTop: 0, color: "var(--ink-2)" }}>What do you want to put on the edge device?</p>
      {items.map(([k, t, d]) => (
        <div key={k} className="check" style={{ cursor: "pointer" }}
          onClick={() => { Modal.close(); if (k === "import") importExisting(nodeId || null); else fire(createGuest(k, nodeId || null)); }}>
          <div><b>{t}</b><small>{d}</small></div></div>
      ))}
    </Frame>,
  );
}

/* ---- create VM / LXC ---- */
export async function createGuest(kind: string, nodeId?: Any) {
  const nodes = DB.nodes.filter((n: Any) => n.status !== "offline");
  if (!nodes.length) { toast("No online device", "Bring a device online first", "err"); return; }
  const nn = nodeId ? node(nodeId) : undefined;
  const n0 = nn && nn.status !== "offline" ? nn : nodes[0];
  let opts: Any;
  try { opts = await api("GET", `/api/nodes/${n0.id}/options`); } catch (e: Any) { toast("Cannot read the device", e.message, "err"); return; }
  const kindOk = (i: Any) => (kind === "vm" ? i.kind === "Cloud image" || i.kind === "VM template" : i.kind === "LXC template");
  // only images built for the device's CPU: an amd64 image never boots on an ARM box and vice versa
  const fits = (i: Any, n: Any) => i.compatible !== false && archFits(i.arch, n?.arch);
  const imgFor = (o: Any, n?: Any): Any[] => o.images.filter((i: Any) => kindOk(i) && (!n || fits(i, n)));
  const otherArch = (o: Any, n: Any): Any[] => o.images.filter((i: Any) => kindOk(i) && !fits(i, n));
  const pickImg = (o: Any, n: Any) => {
    const l = imgFor(o, n);
    return ((l.find((i) => i.here && i.approved) || l.find((i) => i.here) || l[0]) || {}).id;
  };
  const storOf = (o: Any): Any[] => (kind === "vm" ? o.vm_storage : o.ct_storage);
  startWizard({
    title: kind === "vm" ? "Create a virtual machine" : "Create an LXC container",
    data: {
      kind, node: n0.id, _opts: opts, name: "", cores: kind === "vm" ? 2 : 1, mem: kind === "vm" ? 2048 : 1024, disk: kind === "vm" ? 16 : 8,
      image: pickImg(opts, n0), net: "dhcp", bridge: (opts.bridges[0] || {}).id || "vmbr0", vlan: "", ip: "", gw: "", dns: DB.settings.guest?.dns || "1.1.1.1 8.8.8.8",
      storage: (storOf(opts)[0] || {}).id || "local",
      docker: false, compose: false, k3s: false, monitor: false, tailscale: false, funnel: false, funnel_port: 80, unpriv: true, yaml: SAMPLE_COMPOSE, env: SAMPLE_ENV, owner: "", tags: "",
    },
    finishLabel: "Create and deploy",
    steps: [
      {
        name: "Basics",
        render: () => (
          <Fragment key="basics">
            <F.Text k="name" label="Name" hint="Lowercase letters, numbers and hyphens. It becomes the hostname and is what people see everywhere." />
            <Sel k="node" label="Edge device" opts={nodes.map((n: Any) => [n.id, `${n.name} — ${site(n.site).name} (${n.cpu}% CPU, ${n.ram}% memory used, ${n.arch || "?"})`])} />
            <F.Text k="owner" label="Owner or team" />
            <F.Text k="tags" label="Tags" hint="Comma separated. Rollouts, playbooks, alert rules and user scopes can target a tag. Stored as Proxmox tags." />
          </Fragment>
        ),
        validate: async (d) => {
          if (!String(d.name ?? "").trim()) return "Give the workload a name.";
          if (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(d.name)) return "Use lowercase letters, numbers and hyphens only.";
          if (DB.workloads.some((w: Any) => w.name === d.name)) return "That name is already used in this network.";
          if (String(d.node) !== String(d._opts._node || n0.id)) {
            d._opts = await api("GET", `/api/nodes/${d.node}/options`); d._opts._node = d.node;
            d.image = pickImg(d._opts, node(d.node)); d.storage = (storOf(d._opts)[0] || {}).id; d.bridge = (d._opts.bridges[0] || {}).id;
          }
          return null;
        },
      },
      {
        name: "Source",
        render: (d) => {
          const n: Any = node(d.node) || {}, list = imgFor(d._opts, n), hidden = otherArch(d._opts, n);
          const na = archOf(n.arch);
          const cur = list.find((i) => String(i.id) === String(d.image));
          return (
            <Fragment key="source">
              {list.length
                ? <Sel k="image" label={kind === "vm" ? "Cloud image or VM template" : "LXC template"}
                    opts={list.map((i) => [i.id, `${i.name} — ${i.here ? "on this device" : "download first"} · ${i.kind}${i.arch ? " · " + i.arch : ""}${i.approved ? "" : " · not approved"}`])} />
                : <div className="banner"><span>△</span><div><b>No {na || ""} {kind === "vm" ? "VM images" : "LXC templates"} for {n.name}</b>
                    Add one built for {na || n.arch || "this device"} on the Images page (a cloud image URL or an LXC template).</div></div>}
              {na ? <p className="hint" style={{ marginTop: -6 }}>
                  Showing images for <b>{na}</b> ({n.name} is {n.arch}).
                  {hidden.length ? ` ${hidden.length} built for ${[...new Set(hidden.map((i) => archOf(i.arch)))].join(" / ")} ${hidden.length === 1 ? "is" : "are"} hidden: ${hidden.length === 1 ? "it" : "they"} would not boot here.` : ""}
                </p> : null}
              {cur ? (cur.here
                ? <div className="banner acc" style={{ marginBottom: 14 }}><span>◈</span><div><b>Cached on {n.name}</b>Nothing has to be downloaded over the site's uplink.</div></div>
                : <div className="banner" style={{ marginBottom: 14 }}><span>△</span><div><b>Not on this device yet</b>It is downloaded to {n.name} first, which can take a while on a slow link.</div></div>) : null}
              {kind === "lxc" ? <F.Check k="unpriv" title="Unprivileged container" sub="Recommended. Docker still works: nesting and keyctl are enabled for you." /> : null}
            </Fragment>
          );
        },
        validate: (d) => {
          const n: Any = node(d.node) || {};
          const i: Any = (d._opts.images || []).find((x: Any) => String(x.id) === String(d.image));
          if (!i) return "Pick an image.";
          if (!fits(i, n)) return `${i.name} is built for ${archOf(i.arch)}; ${n.name} needs an ${archOf(n.arch)} image.`;
          return null;
        },
      },
      {
        name: "Resources",
        render: (d) => {
          const n: Any = node(d.node) || {};
          const after = Math.round(n.ram + (100 * d.mem) / Math.max(1, n.mem_total));
          const stor = storOf(d._opts);
          return (
            <Fragment key="resources">
              <div className="grid g3"><F.Num k="cores" label="vCPU cores" /><F.Num k="mem" label="Memory (MB)" /><F.Num k="disk" label="Disk (GB)" /></div>
              <Sel k="storage" label="Storage pool" opts={stor.map((s) => [s.id, s.label])} />
              <div className={"banner " + (after > 90 ? "" : "acc")}><span>◈</span><div><b>Capacity check on {n.name}</b>Memory is {n.ram}% used of {Math.round(n.mem_total / 1024)} GB. Adding {d.mem} MB takes it to about {after}%{after > 90 ? " — too tight: pick less memory or another device." : "."}</div></div>
            </Fragment>
          );
        },
        validate: (d) => (+d.cores < 1 ? "At least one core." : +d.mem < 256 ? "At least 256 MB of memory." : +d.disk < 2 ? "At least a 2 GB disk." : null),
      },
      {
        name: "Network",
        render: (d) => (
          <Fragment key="network">
            <Sel k="bridge" label="Bridge" opts={d._opts.bridges.map((b: Any) => [b.id, b.label])} />
            <Sel k="net" label="Addressing" opts={[["dhcp", "DHCP from the site router"], ["static", "Static address"]]} />
            {d.net === "static"
              ? <div className="grid g2"><F.Text k="ip" label="Static IP address (CIDR)" ph="192.168.1.50/24" /><F.Text k="gw" label="Gateway" ph="192.168.1.1" /></div> : null}
            <div className="grid g2"><F.Text k="vlan" label="VLAN tag" hint="Leave blank for untagged." /><F.Text k="dns" label="DNS servers" hint="Space separated. Needed when the device itself resolves through Tailscale MagicDNS." /></div>
            <F.Check k="tailscale" title="Join the management overlay"
              sub={"Gives this machine a 100.x address so Ansible and the console reach it directly." + (DB.settings.connectivity?.has_tailscale_authkey ? "" : " Needs a Tailscale auth key in Settings → Connectivity.")} />
            <div className="section-title" style={{ marginTop: 6 }}>Expose to the internet</div>
            <F.Check k="funnel" title="Publish a port with Tailscale Funnel"
              sub={<>Gives the machine a public {"https://<name>.<tailnet>.ts.net"} URL with an automatic certificate. Turns on the overlay above. Your tailnet policy must allow the <code>funnel</code> attribute for this node.</>} />
            {d.funnel
              ? <div className="grid g2"><F.Num k="funnel_port" label="Local port to publish" hint="The port your app listens on inside the machine, e.g. 80 or 8080. Funnel serves it on :443." /><div /></div> : null}
          </Fragment>
        ),
        validate: (d) =>
          d.funnel && !(+d.funnel_port >= 1 && +d.funnel_port <= 65535) ? "Funnel port must be between 1 and 65535"
            : d.net === "static" && !/^\d+\.\d+\.\d+\.\d+\/\d+$/.test(String(d.ip ?? "").trim()) ? "Static address must look like 192.168.1.50/24"
              : d.vlan && !/^\d+$/.test(d.vlan) ? "VLAN tag is a number" : null,
      },
      {
        name: "Software",
        render: (d) => (
          <Fragment key="software">
            <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Tick what should be installed right after the machine boots. Each one runs as an Ansible playbook in the same job.</p>
            <F.Check k="docker" title="Docker Engine" sub="Includes the compose plugin and log rotation." />
            <F.Check k="compose" title="Deploy a compose stack now" sub="Opens a YAML editor with validation on the next step (implies Docker)." />
            <F.Check k="k3s" title="k3s (lightweight Kubernetes)" sub="Single node to start." />
            <F.Check k="monitor" title="Monitoring agents" sub="node_exporter, plus cAdvisor when Docker is present." />
            {kind === "lxc" && (d.docker || d.k3s)
              ? <div className="banner"><span>△</span><div><b>Docker inside LXC</b>The container gets nesting=1 and keyctl=1. k3s inside an LXC needs extra host tweaks; a VM is the safer choice for Kubernetes.</div></div> : null}
          </Fragment>
        ),
      },
      {
        name: "Compose file",
        skipIf: (d) => !d.compose,
        render: (_d, f) => (
          <Init key="compose" f={f} run={(d) => { if (!d.stack_name) d.stack_name = d.name + "-stack"; }}>
            <F.Text k="stack_name" label="Stack name" />
            <YamlEditor f={f} file="docker-compose.yml" kind="compose" auto
              extra={<button className="btn sm ghost" onClick={() => { f.d.yaml = SAMPLE_COMPOSE; f.d._val = null; f.refresh(); fire(wizValidate(f, "compose", true)); }}>Load example</button>} />
            <F.Area k="env" label="Variables (.env)" rows={3} hint="Stored encrypted, written next to the compose file with mode 600." />
            <div className="hint">Validation parses the YAML, runs <code>docker compose config</code> and applies your deployment policy.</div>
          </Init>
        ),
        validate: (d) => yamlGate(d, "compose"),
      },
      {
        name: "Review",
        render: (d) => {
          const n: Any = node(d.node) || {};
          const img: Any = imgFor(d._opts).find((i) => String(i.id) === String(d.image)) || {};
          const sw = [d.docker && "Docker", d.compose && "compose stack", d.k3s && "k3s", d.monitor && "monitoring", (d.tailscale || d.funnel) && "overlay"].filter(Boolean).join(", ") || "none";
          const steps = [
            `Proxmox API · ${kind === "vm" ? (img.kind === "VM template" ? "clone template" : "create VM + import cloud image") : "create container"}`,
            kind === "vm" ? "cloud-init · user " + (DB.settings.guest?.vm_user || "ops") + " + console SSH key + qemu-guest-agent" : "bootstrap · openssh + python",
            "wait for IP and SSH",
            (d.tailscale || d.funnel) && "join_overlay.yml",
            d.funnel && "tailscale funnel :" + d.funnel_port,
            (d.docker || d.compose) && "install_docker.yml",
            d.k3s && "install_k3s.yml",
            d.monitor && "install_exporters.yml",
            d.compose && "docker compose up (health-gated)",
          ].filter(Boolean) as string[];
          return (
            <Fragment key="review">
              <dl className="kv">
                <dt>Name</dt><dd>{d.name}</dd><dt>Type</dt><dd>{kind.toUpperCase()}</dd>
                <dt>Edge device</dt><dd>{n.name} — {site(n.site).name}</dd><dt>Image</dt><dd>{img.name || "?"}</dd>
                <dt>Resources</dt><dd>{d.cores} vCPU · {d.mem} MB · {d.disk} GB on {d.storage}</dd>
                <dt>Network</dt><dd>{d.bridge} · {d.net === "dhcp" ? "DHCP" : d.ip}{d.vlan ? " · VLAN " + d.vlan : ""} · DNS {d.dns}</dd>
                <dt>Software</dt><dd>{sw}</dd>
                <dt>Internet</dt><dd>{d.funnel ? `Tailscale Funnel → port ${d.funnel_port} (public HTTPS)` : "not exposed"}</dd>
                {d.relaxed ? <><dt>Validation</dt><dd>relaxed (Admin override)</dd></> : null}
              </dl>
              <div className="section-title">What will run</div>
              <pre className="log">{steps.map((p, i) => <Fragment key={i}>{i ? "\n" : ""}<span className="g">▸</span> {p}</Fragment>)}</pre>
            </Fragment>
          );
        },
      },
    ],
    onFinish: (d) => job("POST", "/api/workloads", {
      kind, node: +d.node, name: d.name, owner: d.owner, tags: d.tags, image: +d.image, cores: +d.cores, mem: +d.mem,
      disk: +d.disk, storage: d.storage, bridge: d.bridge, net: d.net, ip: d.ip, gw: d.gw, vlan: d.vlan, dns: d.dns, tailscale: !!(d.tailscale || d.funnel),
      funnel: !!d.funnel, funnel_port: +d.funnel_port || 80, relaxed: !!d.relaxed,
      docker: !!(d.docker || d.compose), compose: !!d.compose, k3s: !!d.k3s, monitor: !!d.monitor, unpriv: d.unpriv !== false,
      yaml: d.compose ? d.yaml : "", env: d.compose ? d.env : "", stack_name: d.stack_name || "",
    }),
  });
}

/* ---- import existing machine ---- */
function Conn({ r }: { r: Any }) {
  return (
    <div className="vres">
      <div className="vh" style={{ background: r.ok ? "var(--ok-soft)" : "var(--bad-soft)", color: r.ok ? "var(--ok)" : "var(--bad)" }}>{r.ok ? "Reachable" : "Not reachable"}</div>
      <ul>{(r.checks || []).map((c: Any, i: number) => (
        <li key={i}><span className={"tag " + c.tag}>{c.tag === "ok" ? "ok" : c.tag === "wrn" ? "warn" : "error"}</span><span>{c.msg}</span></li>
      ))}</ul>
    </div>
  );
}
function ConnTest({ f }: { f: Form }) {
  const [st, setSt] = useState<Any>(null); // null | "busy" | {error}
  const test = async () => {
    const d = f.d;
    setSt("busy");
    try {
      const r = await api("POST", "/api/workloads/test-connection",
        { ip: String(d.ip ?? "").trim(), user: String(d.user ?? "").trim(), auth: d.auth, password: d.password, node: d.node ? +d.node : null });
      d._test = r; setSt(null); f.refresh();
      toast(r.ok ? "Connection works" : "Connection failed", r.ok && r.facts ? r.facts.os : "", r.ok ? "" : "err");
    } catch (e: Any) { setSt({ error: e.message }); }
  };
  return (
    <>
      <div className="btnrow"><button className="btn" onClick={() => fire(test())}>Test connection</button></div>
      <div id="conn" style={{ marginTop: 12 }}>
        {st === "busy" ? <><span className="spin" /> Connecting…</>
          : st && st.error != null ? <div className="vres"><div className="vh">{st.error}</div></div>
            : f.d._test ? <Conn r={f.d._test} /> : null}
      </div>
    </>
  );
}

export function importExisting(nodeId?: Any, ip?: string, name?: string) {
  startWizard({
    title: "Import a machine that already exists",
    data: {
      ip: ip || "", user: "root", auth: "key", password: "", node: nodeId || (DB.nodes[0] || {}).id || "", docker: false, compose: false, k3s: false, monitor: false, tailscale: false,
      yaml: SAMPLE_COMPOSE, env: SAMPLE_ENV, name: name || "", _test: null,
    },
    finishLabel: "Import and run",
    steps: [
      {
        name: "Connection",
        render: (d, f) => (
          <Fragment key="connection">
            <F.Text k="ip" label="IP address or hostname" hint="Reachable from this console directly, or from the edge device you pick below." />
            <F.Text k="user" label="SSH user" hint="root, or a user with passwordless sudo." />
            <Sel k="auth" label="Authentication" opts={[["key", "The console SSH key is already authorised"], ["password", "Password — used once to install the console key, never stored"]]} />
            {d.auth === "password"
              ? <F.Pass k="password" label="Password" />
              : <div className="hint" style={{ margin: "-6px 0 14px" }}>Add this to <code>~/.ssh/authorized_keys</code> first: <Copyable v={DB.meta.pubkey} cls="block" /></div>}
            <Sel k="node" label="Reach it through" opts={[["", "Directly from the console"] as Opt].concat(DB.nodes.map((n: Any) => [n.id, n.name]))} />
            <ConnTest f={f} />
          </Fragment>
        ),
        validate: (d) => (!String(d.ip ?? "").trim() ? "Enter an IP address or hostname." : d.auth === "password" && !d.password ? "Enter the password." : null),
      },
      {
        name: "Details",
        render: (d, f) => {
          const facts = d._test && d._test.facts;
          return (
            <Init key="details" f={f} run={(x) => {
              if (!x.name) x.name = ("imported-" + (String(x.ip ?? "").split(".").pop() || "host")).toLowerCase();
              if (!x.owner) x.owner = "";
              if (!x.tags) x.tags = "imported";
            }}>
              {facts
                ? <div className="banner acc"><span>◈</span><div><b>Detected: {facts.os} · {facts.cores} vCPU · {Math.round((facts.mem / 1024) * 10) / 10} GB · {facts.disk} GB disk</b>{facts.docker ? "Docker " + facts.docker + " is installed." : "No container runtime found."} {facts.qga ? "QEMU guest agent is running." : ""}</div></div>
                : <div className="banner"><span>△</span><div><b>Connection not tested</b>Go back and test it to see what is on the machine.</div></div>}
              <F.Text k="name" label="Name in Edge Compute" />
              <F.Text k="owner" label="Owner or team" />
              <F.Text k="tags" label="Tags" />
            </Init>
          );
        },
        validate: (d) => (!/^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/.test(d.name) ? "Use lowercase letters, numbers and hyphens only."
          : DB.workloads.some((w: Any) => w.name === d.name && w.ip !== d.ip) ? "That name is already used." : null),
      },
      {
        name: "Software",
        render: (_d, f) => (
          <Init key="software" f={f} run={(x) => { x.docker = x.docker || !!(x._test && x._test.facts && x._test.facts.docker); }}>
            <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Anything to install on this machine now?</p>
            <F.Check k="docker" title="Docker Engine" sub="Installs the engine and the compose plugin." />
            <F.Check k="compose" title="Deploy a compose stack now" sub="You get a YAML editor with validation on the next step." />
            <F.Check k="k3s" title="k3s (lightweight Kubernetes)" sub="" />
            <F.Check k="monitor" title="Monitoring agents" sub="node_exporter, plus cAdvisor when Docker is present." />
            <F.Check k="tailscale" title="Join the management overlay" sub="" />
          </Init>
        ),
      },
      {
        name: "Compose file",
        skipIf: (d) => !d.compose,
        render: (_d, f) => (
          <Fragment key="compose">
            <YamlEditor f={f} file="docker-compose.yml" kind="compose" auto />
            <F.Area k="env" label="Variables (.env)" rows={3} />
          </Fragment>
        ),
        validate: (d) => yamlGate(d, "compose"),
      },
      {
        name: "Review",
        render: (d) => (
          <dl key="review" className="kv"><dt>Address</dt><dd>{d.ip} as {d.user}</dd><dt>Name</dt><dd>{d.name}</dd>
            <dt>Reached through</dt><dd>{d.node ? node(d.node)?.name ?? "" : "direct"}</dd>
            <dt>Will install</dt><dd>{[d.docker && "Docker", d.compose && "compose stack", d.k3s && "k3s", d.monitor && "monitoring", d.tailscale && "overlay"].filter(Boolean).join(", ") || "nothing"}</dd></dl>
        ),
      },
    ],
    onFinish: (d) => job("POST", "/api/workloads/import", {
      ip: String(d.ip ?? "").trim(), user: String(d.user ?? "").trim(), auth: d.auth, password: d.password, node: d.node ? +d.node : null,
      name: d.name, owner: d.owner, tags: d.tags, docker: !!(d.docker || d.compose), compose: !!d.compose, k3s: !!d.k3s, monitor: !!d.monitor,
      tailscale: !!d.tailscale, yaml: d.compose ? d.yaml : "", env: d.compose ? d.env : "", relaxed: !!d.relaxed,
    }),
  });
}

/* ---- install software on an existing workload ---- */
export function installSoftware(id: Any) {
  const w = wl(id);
  if (!w) return;
  startWizard({
    title: `Install software on ${w.name}`,
    data: { docker: !w.docker, compose: false, k3s: false, monitor: false, tailscale: false, yaml: SAMPLE_COMPOSE, env: SAMPLE_ENV, _wid: w.id },
    finishLabel: "Install",
    steps: [
      {
        name: "Choose",
        render: () => (
          <Fragment key="choose">
            <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Running {w.os} on {nodeOf(w).name}.</p>
            {!w.ssh_ok ? <div className="banner"><span>△</span><div><b>No SSH access yet</b>Use “Enable management” on this workload first, or the install will fail.</div></div> : null}
            <F.Check k="docker" title="Docker Engine" sub="Engine plus compose plugin, with log rotation set to 10 MB × 3 files." />
            <F.Check k="compose" title="Deploy a compose stack straight away" sub="" />
            <F.Check k="k3s" title="k3s (lightweight Kubernetes)" sub="" />
            <F.Check k="monitor" title="Monitoring agents" sub="node_exporter, plus cAdvisor when Docker is present." />
            <F.Check k="tailscale" title="Join the management overlay" sub="" />
          </Fragment>
        ),
        validate: (d) => (!(d.docker || d.compose || d.k3s || d.monitor || d.tailscale) ? "Pick something to install." : null),
      },
      {
        name: "Compose file",
        skipIf: (d) => !d.compose,
        render: (_d, f) => (
          <Init key="compose" f={f} run={(x) => { if (!x.stack_name) x.stack_name = w.name + "-stack"; }}>
            <F.Text k="stack_name" label="Stack name" />
            <YamlEditor f={f} file="docker-compose.yml" kind="compose" auto />
            <F.Area k="env" label="Variables (.env)" rows={3} />
          </Init>
        ),
        validate: (d) => yamlGate(d, "compose"),
      },
    ],
    onFinish: (d) => job("POST", `/api/workloads/${w.id}/install`, {
      docker: !!(d.docker || d.compose), compose: !!d.compose, k3s: !!d.k3s, monitor: !!d.monitor,
      tailscale: !!d.tailscale, yaml: d.compose ? d.yaml : "", env: d.compose ? d.env : "", stack_name: d.stack_name || "", relaxed: !!d.relaxed,
    }),
  });
}

/* ---- deploy / edit a stack ---- */
async function gitImport(f: Form, kind: string) {
  const d = f.d;
  try {
    const r = await api("POST", "/api/import-git", { url: d.git, ref: d.gitref || "main", path: d.gitpath });
    d.yaml = r.text; d._val = null; f.refresh();
    toast("Fetched from Git", d.gitpath);
    fire(wizValidate(f, kind, true));
  } catch (e: Any) { toast("Import failed", e.message, "err"); }
}

function ManifestStep({ f, st, w, kind }: { f: Form; st: Any; w: Any; kind: string }) {
  const [git, setGit] = useState(false);
  const init = useRef(false);
  if (!init.current) {
    init.current = true;
    if (!st && !f.d.name) f.d.name = w.name + "-stack";
    if (!f.d.gitref) f.d.gitref = "main";
  }
  const sample = kind === "k8s" ? SAMPLE_K8S : SAMPLE_COMPOSE;
  return (
    <>
      {st
        ? (st.discovered ? <div className="banner acc"><span>◈</span><div><b>Found on the host, not deployed from here</b>Redeploying puts it under management: the file is stored in /opt/edge-compute/stacks/{st.name} and versioned.</div></div> : null)
        : <F.Text k="name" label="Stack name" />}
      <YamlEditor f={f} file={kind === "k8s" ? "manifest.yaml" : "docker-compose.yml"} kind={kind} auto
        extra={<>
          <span style={{ color: "var(--ink-3)" }}>target: {w.name}</span>{" "}
          <button className="btn sm ghost" onClick={() => { f.d.yaml = sample; f.d._val = null; f.refresh(); fire(wizValidate(f, kind, true)); }}>Load example</button>{" "}
          <button className="btn sm ghost" onClick={() => setGit(true)}>Import from Git</button>{" "}
        </>} />
      <div id="gitbox" className="card" style={{ display: git ? "block" : "none", marginTop: 12 }}><div className="bd">
        <div className="grid g3">
          <F.Text k="git" label="Repository or raw file URL" ph="https://github.com/org/repo" />
          <F.Text k="gitref" label="Branch or tag" />
          <F.Text k="gitpath" label="Path in the repo" />
        </div>
        <button className="btn sm" onClick={() => fire(gitImport(f, kind))}>Fetch file</button>
      </div></div>
    </>
  );
}

export async function deployStack(wid: Any, kind: string, stackId?: Any) {
  const w = wl(wid);
  if (!w) return;
  let st: Any = null;
  if (stackId) { try { st = await api("GET", `/api/stacks/${stackId}`); } catch (e: Any) { toast("Cannot load stack", e.message, "err"); return; } }
  const sample = kind === "k8s" ? SAMPLE_K8S : SAMPLE_COMPOSE;
  startWizard({
    title: st ? `Edit ${st.name}` : kind === "k8s" ? "Apply a Kubernetes manifest" : "Deploy a compose stack",
    data: {
      yaml: st ? st.content || "" : sample, name: st ? st.name : "", env: st ? st.env : kind === "k8s" ? "" : SAMPLE_ENV, pull: st ? st.pull : "missing",
      rollback: st ? st.rollback : true, _wid: w.id, _sid: st ? st.id : null, git: "", gitpath: kind === "k8s" ? "manifest.yaml" : "docker-compose.yml",
    },
    finishLabel: st ? "Redeploy" : "Deploy",
    steps: [
      {
        name: "Manifest",
        render: (_d, f) => <ManifestStep key="manifest" f={f} st={st} w={w} kind={kind} />,
        validate: async (d) => {
          if (!st && !/^[a-z0-9_-]+$/.test((d.name || "").toLowerCase().replace(/[^a-z0-9_-]+/g, "-"))) return "Give the stack a name.";
          return yamlGate(d, kind);
        },
      },
      {
        name: "Variables",
        skipIf: () => kind === "k8s",
        render: () => (
          <Fragment key="variables">
            <F.Area k="env" label="Environment file (.env)" rows={6}
              hint={st ? "Saved values show as ••••••••. Leave them as they are to keep them, or type a new value." : "Values are stored encrypted and written to the device with mode 600."} />
            <Sel k="pull" label="Image pull policy" opts={[["missing", "Pull only what is missing"], ["always", "Always pull"], ["never", "Never pull — use the images already on the device"]]} />
            <F.Check k="rollback" title="Roll back automatically if health checks fail" sub="Keeps the previous version running if the new one is not healthy within 90 seconds." />
          </Fragment>
        ),
        validate: (d) => yamlGate(d, "compose"),
      },
      {
        name: "Review",
        render: (d) => {
          const r = d._val || { errors: [], warnings: [], services: [] };
          return (
            <Fragment key="review">
              <dl className="kv"><dt>Target</dt><dd>{w.name} — {nodeOf(w).name}</dd>
                <dt>Stack</dt><dd>{st ? st.name : d.name || w.name + "-stack"}</dd>
                <dt>{kind === "k8s" ? "Objects" : "Services"}</dt><dd>{(r.services || []).join(", ") || "—"}</dd>
                <dt>Warnings</dt><dd>{(r.warnings || []).length}</dd>
                {kind === "k8s" ? null : <><dt>Rollback</dt><dd>{d.rollback ? "automatic" : "manual"}</dd></>}</dl>
              {st && st.version
                ? <div className="banner acc"><span>◈</span><div><b>This creates version {st.version + 1}</b>Version {st.version} stays available for one-click rollback.</div></div> : null}
            </Fragment>
          );
        },
      },
    ],
    onFinish: (d) => job("POST", "/api/stacks", {
      workload: w.id, kind, name: d.name, yaml: d.yaml, env: d.env || "", pull: d.pull || "missing", rollback: !!d.rollback, stack: st ? st.id : null, relaxed: !!d.relaxed,
    }),
  });
}
